import React, { useState, useEffect } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { 
  Plus, Edit, Trash, X, PieChart, Filter, Loader, GripVertical, 
  ListFilter, Download, Grid, List, Search, Settings, ArrowDownUp
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Customer } from '../lib/customers';
import SelectCustomerModal from '../components/manage/SelectCustomerModal';

interface Stage {
  id: number;
  name: string;
  position?: number;
}

interface Pipeline {
  id: number;
  business_id: string;
  pipeline_name: string;
  stages?: Stage[];
}

interface Opportunity {
  id: number;
  business_id: string;
  customer_id: string;
  title: string;
  pipeline_id: number;
  stage_id: number;
  value: number;
  business_name?: string | null;
  source?: string | null;
  status: string;
  created_at: string;
  custom_fields?: string | null; // JSON string of custom fields
}

export default function CRM() {
  const { selectedBusiness } = useBusiness();
  const [activeTab, setActiveTab] = useState('opportunities');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentPipelineId, setCurrentPipelineId] = useState<number | null>(null);
  const [pipelineName, setPipelineName] = useState('');
  const [stages, setStages] = useState<Stage[]>([{ id: 1, name: '', position: 1 }]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draggedStageId, setDraggedStageId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'list'>('all');
  const [isOpportunityModalOpen, setIsOpportunityModalOpen] = useState(false);
  const [isOpportunityInfoModalOpen, setIsOpportunityInfoModalOpen] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isCustomFieldsModalOpen, setIsCustomFieldsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [opportunityTitle, setOpportunityTitle] = useState('');
  const [selectedOpportunityForInfo, setSelectedOpportunityForInfo] = useState<Opportunity | null>(null);
  const [opportunitiesViewPipeline, setOpportunitiesViewPipeline] = useState<Pipeline | null>(null);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [opportunityValue, setOpportunityValue] = useState(0);
  const [businessName, setBusinessName] = useState('');
  const [opportunitySource, setOpportunitySource] = useState('');
  const [customFields, setCustomFields] = useState<Array<{id: string, name: string, type: string, value: string}>>([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<Array<{id: string, name: string, type: string}>>([]);

  // Function to fetch pipelines from the database
  const fetchPipelines = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    const supabaseClient = supabase; // Create local reference for type safety

    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabaseClient
        .from('pipelines')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('pipeline_name', { ascending: true });

      if (error) {
        throw error;
      }
      
      const fetchedPipelines = data || [];
      
      // Transform pipelines to include parsed stages (stages are stored as JSON in the stages column)
      const pipelinesWithStages = fetchedPipelines.map(pipeline => ({
        ...pipeline,
        stages: Array.isArray(pipeline.stages) ? pipeline.stages : []
      }));

      setPipelines(pipelinesWithStages);
      
    } catch (err) {
      console.error('Error fetching pipelines:', err);
      setError('Failed to load pipelines. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch opportunities from the database
  const fetchOpportunities = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      const { data, error } = await supabase
        .from('pipeline_opportunities')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setOpportunities(data || []);
      
    } catch (err) {
      console.error('Error fetching opportunities:', err);
    }
  };

  // Load custom field definitions from localStorage
  const loadCustomFieldDefinitions = () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const stored = localStorage.getItem(`customFieldDefinitions_${selectedBusiness.id}`);
      if (stored) {
        const definitions = JSON.parse(stored);
        setCustomFieldDefinitions(definitions);
      }
    } catch (error) {
      console.error('Error loading custom field definitions:', error);
    }
  };

  // Save custom field definitions to localStorage
  const saveCustomFieldDefinitions = (definitions: Array<{id: string, name: string, type: string}>) => {
    if (!selectedBusiness?.id) return;
    
    try {
      localStorage.setItem(`customFieldDefinitions_${selectedBusiness.id}`, JSON.stringify(definitions));
    } catch (error) {
      console.error('Error saving custom field definitions:', error);
    }
  };

  // Initialize custom fields based on definitions
  const initializeCustomFields = () => {
    const initialFields = customFieldDefinitions.map(def => ({
      id: def.id,
      name: def.name,
      type: def.type,
      value: ''
    }));
    setCustomFields(initialFields);
  };

  // Initialize custom fields when definitions change
  useEffect(() => {
    initializeCustomFields();
  }, [customFieldDefinitions]);

  // Load pipelines, opportunities, and custom field definitions when the component mounts or business changes
  useEffect(() => {
    if (selectedBusiness?.id) {
      fetchPipelines();
      fetchOpportunities();
      loadCustomFieldDefinitions();
    }
  }, [selectedBusiness?.id]);

  // Set default pipeline for opportunities view when pipelines are loaded
  useEffect(() => {
    if (pipelines.length > 0 && !opportunitiesViewPipeline) {
      setOpportunitiesViewPipeline(pipelines[0]);
    }
  }, [pipelines, opportunitiesViewPipeline]);

  const addStage = () => {
    const newPosition = stages.length + 1;
    setStages([...stages, { id: Date.now(), name: '', position: newPosition }]);
  };

  const updateStageName = (id: number, name: string) => {
    setStages(stages.map(stage => 
      stage.id === id ? { ...stage, name } : stage
    ));
  };

  const removeStage = (id: number) => {
    if (stages.length > 1) {
      const filteredStages = stages.filter(stage => stage.id !== id);
      // Reorder positions after removing
      const updatedStages = filteredStages.map((stage, index) => ({
        ...stage,
        position: index + 1
      }));
      setStages(updatedStages);
    }
  };

  const handleDragStart = (e: React.DragEvent, id: number) => {
    setDraggedStageId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Set a ghost image for dragging (optional)
    const draggedElement = e.currentTarget as HTMLElement;
    if (draggedElement) {
      const ghost = draggedElement.cloneNode(true) as HTMLElement;
      ghost.style.position = 'absolute';
      ghost.style.top = '-1000px';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 0, 0);
      setTimeout(() => {
        document.body.removeChild(ghost);
      }, 0);
    }
  };

  const handleDragOver = (e: React.DragEvent, id: number) => {
    e.preventDefault();
    if (draggedStageId === null || draggedStageId === id) return;

    // Find positions of the dragged and target stages
    const draggedStage = stages.find(stage => stage.id === draggedStageId);
    const targetStage = stages.find(stage => stage.id === id);
    
    if (!draggedStage || !targetStage) return;
    
    // Create a new array with reordered stages
    const draggedPosition = draggedStage.position || 0;
    const targetPosition = targetStage.position || 0;
    
    const newStages = stages.map(stage => {
      if (stage.id === draggedStageId) {
        return { ...stage, position: targetPosition };
      } else if (
        (draggedPosition < targetPosition && 
          stage.position && 
          stage.position > draggedPosition && 
          stage.position <= targetPosition)
      ) {
        return { ...stage, position: (stage.position || 0) - 1 };
      } else if (
        (draggedPosition > targetPosition && 
          stage.position && 
          stage.position < draggedPosition && 
          stage.position >= targetPosition)
      ) {
        return { ...stage, position: (stage.position || 0) + 1 };
      }
      return stage;
    });
    
    // Sort by position and update
    setStages([...newStages].sort((a, b) => (a.position || 0) - (b.position || 0)));
  };

  const handleDragEnd = () => {
    setDraggedStageId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedBusiness?.id || !pipelineName.trim() || !supabase) {
      return;
    }
    
    // Validate that at least one stage has a name
    const validStages = stages.filter(stage => stage.name.trim() !== '');
    if (validStages.length === 0) {
      alert('Please add at least one stage with a name');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Format stages for storage - ensure stages have position property
      const formattedStages = validStages.map((stage, index) => ({
        id: stage.id,
        name: stage.name.trim(),
        position: stage.position || index + 1
      }));
      
      // Data to insert/update
      const pipelineData = { 
        business_id: selectedBusiness.id,
        pipeline_name: pipelineName.trim(),
        stages: formattedStages
      };
      
      let error;
      
      if (isEditMode && currentPipelineId) {
        // Update existing pipeline
        const { error: updateError } = await supabase
          .from('pipelines')
          .update(pipelineData)
          .eq('id', currentPipelineId);
        
        error = updateError;
      } else {
        // Create new pipeline
        const { error: insertError } = await supabase
          .from('pipelines')
          .insert([pipelineData])
          .select();
        
        error = insertError;
      }
      
      if (error) throw error;
      
      // Refresh the pipelines list
      fetchPipelines();
      
      // Close modal and reset form
      handleCloseModal();
      
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} pipeline:`, err);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} pipeline. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (editMode: boolean = false, pipeline?: Pipeline) => {
    setIsEditMode(editMode);
    
    if (editMode && pipeline) {
      // Load pipeline data for editing
      setCurrentPipelineId(pipeline.id);
      setPipelineName(pipeline.pipeline_name);
      
      // Load stages if they exist, otherwise create a default one
      if (pipeline.stages && pipeline.stages.length > 0) {
        setStages(pipeline.stages.map(stage => ({
          ...stage,
          id: typeof stage.id === 'number' ? stage.id : Date.now() + Math.floor(Math.random() * 1000)
        })));
      } else {
        setStages([{ id: Date.now(), name: '', position: 1 }]);
      }
    } else {
      // Reset for create mode
      setCurrentPipelineId(null);
      setPipelineName('');
      setStages([{ id: 1, name: '', position: 1 }]);
    }
    
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentPipelineId(null);
    setPipelineName('');
    setStages([{ id: 1, name: '', position: 1 }]);
    setOpportunityValue(0);
    setBusinessName('');
    setOpportunitySource('');
    setCustomFields([]);
  };

  // Function to delete a pipeline
  const handleDeletePipeline = async (id: number) => {
    if (!supabase || !window.confirm('Are you sure you want to delete this pipeline?')) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('pipelines')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      // Refresh the list
      fetchPipelines();
      
    } catch (err) {
      console.error('Error deleting pipeline:', err);
      alert('Failed to delete the pipeline. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // This function will render the kanban board stage columns
  const renderStageColumns = () => {
    if (!opportunitiesViewPipeline || !opportunitiesViewPipeline.stages || opportunitiesViewPipeline.stages.length === 0) {
      return (
        <div className="flex space-x-4 overflow-x-auto pb-4">
          {[1, 2, 3, 4, 5].map(num => (
            <div key={num} className="bg-white border border-gray-200 rounded-md p-3 min-w-[250px] flex-shrink-0">
              <h3 className="font-medium text-gray-700 mb-1">{num}</h3>
              <div className="text-xs text-gray-500">0 Opportunities <span className="ml-2">C$0.00</span></div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="flex space-x-4 overflow-x-auto pb-4">
        {opportunitiesViewPipeline.stages.map(stage => {
          // Filter opportunities for this stage
          const stageOpportunities = opportunities.filter(opp => 
            opp.pipeline_id === opportunitiesViewPipeline.id && 
            opp.stage_id === stage.id
          );
          
          // Calculate total value for this stage
          const totalValue = stageOpportunities.reduce((sum, opp) => sum + (opp.value || 0), 0);
          
          return (
            <div key={stage.id} className="bg-gray-100 rounded-md p-3 min-w-[300px] flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-700">{stage.name}</h3>
                <div className="text-xs text-gray-500">
                  {stageOpportunities.length} Opportunities <span className="ml-2">C${totalValue.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Opportunity Cards */}
              <div className="space-y-3">
                {stageOpportunities.map(opportunity => (
                  <div 
                    key={opportunity.id} 
                    className="bg-white border border-gray-200 rounded-md p-3 relative cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleOpenOpportunityInfo(opportunity)}
                  >
                    {/* Close button */}
                    <button 
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs hover:bg-gray-800"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the card click
                        // TODO: Handle opportunity deletion
                        console.log('Delete opportunity:', opportunity.id);
                      }}
                    >
                      ×
                    </button>
                    
                    {/* Opportunity Title */}
                    <h4 className="font-medium text-gray-900 mb-3 pr-8">{opportunity.title}</h4>
                    
                    {/* Opportunity Details */}
                    <div className="space-y-2 mb-3">
                      <div className="flex text-sm">
                        <span className="text-blue-600 w-32">Opportunity Source:</span>
                        <span className="text-gray-600">{opportunity.source || 'Direct'}</span>
                      </div>
                      <div className="flex text-sm">
                        <span className="text-blue-600 w-32">Opportunity Value:</span>
                        <span className="text-gray-600">C${(opportunity.value || 0).toFixed(2)}</span>
                      </div>
                    </div>
                    
                    {/* Action Icons */}
                    <div className="flex space-x-2 pt-2 border-t border-gray-100">
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Call"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Message"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-blue-600 hover:text-blue-800" 
                        title="Favorite"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Document"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Email"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Calendar"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderNoOpportunitiesState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-40 h-40 mb-6">
        <img 
          src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgdmlld0JveD0iMCAwIDE2MCAxNjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGcgb3BhY2l0eT0iMC44Ij4KICAgIDxjaXJjbGUgY3g9IjgwIiBjeT0iODAiIHI9IjgwIiBmaWxsPSIjRjVGNUY1Ii8+CiAgICA8cGF0aCBkPSJNMTE3LjQ0OSA0Mi41NTExQzEyMS4zNjMgNDYuNDY1MSAxMjEuMzYzIDUyLjc5ODIgMTE3LjQ0OSA1Ni43MTIyTDU2LjcxMjIgMTE3LjQ0OUM1Mi43OTgyIDEyMS4zNjMgNDYuNDY1MSAxMjEuMzYzIDQyLjU1MTEgMTE3LjQ0OUMzOC42MzcxIDExMy41MzUgMzguNjM3MSAxMDcuMjAyIDQyLjU1MTEgMTAzLjI4OEwxMDMuMjg4IDQyLjU1MTFDMTAzLjI4OCA0Mi41NTExIDEwNy4yMDIgMzguNjM3MSAxMTcuNDQ5IDQyLjU1MTFaIiBzdHJva2U9IiNCRUJFQkUiIHN0cm9rZS13aWR0aD0iMiIvPgogICAgPHJlY3QgeD0iNjUiIHk9IjY1IiB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHJ4PSIxIiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSIjQkVCRUJFIiBzdHJva2Utd2lkdGg9IjIiLz4KICAgIDxjaXJjbGUgY3g9IjgwIiBjeT0iODAiIHI9IjEwIiBzdHJva2U9IiNGRjZCNkIiIHN0cm9rZS13aWR0aD0iMiIvPgogICAgPHBhdGggZD0iTTc1IDgwTDc4IDgzTDg1IDc2IiBzdHJva2U9IiNGRjZCNkIiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CiAgPC9nPgo8L3N2Zz4K" 
          alt="No opportunities found" 
          className="w-full h-full"
        />
      </div>
      <h3 className="text-lg font-medium text-gray-700">No Opportunities Match the Filter's Set</h3>
      <p className="text-gray-500 text-center mt-2 max-w-md">
        Try adjusting your filters to find the Opportunities you're looking for.
      </p>
      <div className="flex mt-4 space-x-3">
        <button className="flex items-center bg-blue-500 text-white px-4 py-2 rounded-md">
          <Filter size={16} className="mr-2" /> Update Filter
        </button>
        <button className="flex items-center bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md">
          <X size={16} className="mr-2" /> Clear Filter
        </button>
      </div>
    </div>
  );

  // Handle customer selection and set default title
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setOpportunityTitle(customer.first_name); // Set default title to customer's first name
    setIsCustomerModalOpen(false);
  };

  // Handle customer removal
  const handleCustomerRemove = () => {
    setSelectedCustomer(null);
    setOpportunityTitle(''); // Clear title when customer is removed
  };

  // Handle pipeline selection and clear stage
  const handlePipelineSelect = (pipeline: Pipeline | null) => {
    setSelectedPipeline(pipeline);
    setSelectedStage(null); // Clear stage when pipeline changes
  };

  // Handle opening modal with current pipeline context
  const handleOpenOpportunityModal = (currentPipeline?: Pipeline) => {
    setIsOpportunityModalOpen(true);
    
    // Initialize custom fields based on definitions
    initializeCustomFields();
    
    if (currentPipeline) {
      setSelectedPipeline(currentPipeline);
      // Auto-select first stage if available
      if (currentPipeline.stages && currentPipeline.stages.length > 0) {
        setSelectedStage(currentPipeline.stages[0]);
      }
    } else if (pipelines.length > 0) {
      // Default to first pipeline if no specific pipeline context
      const firstPipeline = pipelines[0];
      setSelectedPipeline(firstPipeline);
      // Auto-select first stage if available
      if (firstPipeline.stages && firstPipeline.stages.length > 0) {
        setSelectedStage(firstPipeline.stages[0]);
      }
    }
  };

  // Handle creating a new opportunity (now with database)
  const handleCreateOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Create opportunity clicked');
    console.log('Form values:', {
      selectedCustomer,
      selectedPipeline,
      selectedStage,
      opportunityTitle,
      opportunityValue,
      businessName,
      opportunitySource,
      customFields
    });
    
    if (!selectedCustomer || !selectedPipeline || !selectedStage || !selectedBusiness?.id || !supabase) {
      console.log('Validation failed:', {
        hasCustomer: !!selectedCustomer,
        hasPipeline: !!selectedPipeline,
        hasStage: !!selectedStage,
        hasBusinessId: !!selectedBusiness?.id,
        hasSupabase: !!supabase
      });
      return;
    }

    try {
      console.log('Creating opportunity in database...');
      setIsLoading(true);
      
      const opportunityData = {
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        title: opportunityTitle,
        pipeline_id: selectedPipeline.id,
        stage_id: selectedStage.id,
        value: opportunityValue,
        business_name: businessName || null,
        source: opportunitySource || null,
        status: 'active',
        custom_fields: customFields.length > 0 ? JSON.stringify(customFields) : null
      };
      
      console.log('Opportunity data to insert:', opportunityData);
      
      // Create the opportunity in database
      const { data, error } = await supabase
        .from('pipeline_opportunities')
        .insert(opportunityData)
        .select()
        .single();

      console.log('Supabase response:', { data, error });

      if (error) throw error;

      // Add the new opportunity to the local state
      setOpportunities(prev => [...prev, data]);
      console.log('Opportunity created successfully:', data);
      
      // Close the modal and reset
      handleModalClose();
      
    } catch (err) {
      console.error('Error creating opportunity:', err);
      setError('Failed to create opportunity. Please try again.');
      alert('Error creating opportunity: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle adding a custom field
  const handleAddCustomField = (name: string, type: string) => {
    const newFieldDef = {
      id: Date.now().toString(),
      name,
      type
    };
    
    // Add to definitions and save persistently
    const updatedDefinitions = [...customFieldDefinitions, newFieldDef];
    setCustomFieldDefinitions(updatedDefinitions);
    saveCustomFieldDefinitions(updatedDefinitions);
    
    // Add to current fields with empty value
    const newField = {
      id: newFieldDef.id,
      name: newFieldDef.name,
      type: newFieldDef.type,
      value: ''
    };
    setCustomFields(prev => [...prev, newField]);
  };

  // Handle updating custom field value
  const handleUpdateCustomField = (id: string, value: string) => {
    setCustomFields(prev => 
      prev.map(field => field.id === id ? { ...field, value } : field)
    );
  };

  // Handle removing a custom field definition
  const handleRemoveCustomField = (id: string) => {
    // Remove from definitions and save
    const updatedDefinitions = customFieldDefinitions.filter(def => def.id !== id);
    setCustomFieldDefinitions(updatedDefinitions);
    saveCustomFieldDefinitions(updatedDefinitions);
    
    // Remove from current fields
    setCustomFields(prev => prev.filter(field => field.id !== id));
  };

  // Reset modal state when closing
  const handleModalClose = () => {
    setIsOpportunityModalOpen(false);
    setSelectedCustomer(null);
    setOpportunityTitle('');
    setSelectedPipeline(null);
    setSelectedStage(null);
    setOpportunityValue(0);
    setBusinessName('');
    setOpportunitySource('');
    // Reset custom field values but keep the definitions
    initializeCustomFields();
  };

  // Handle opening opportunity info modal
  const handleOpenOpportunityInfo = async (opportunity: Opportunity) => {
    try {
      setSelectedOpportunityForInfo(opportunity);
      
      // Find the customer for this opportunity
      if (!supabase) return;
      const { data: customerData } = await supabase
        .from('customers')
        .select('*')
        .eq('id', opportunity.customer_id)
        .single();
      
      // Find the pipeline and stage
      const pipeline = pipelines.find(p => p.id === opportunity.pipeline_id);
      const stage = pipeline?.stages?.find(s => s.id === opportunity.stage_id);
      
      // Parse custom fields if they exist and merge with current definitions
      let mergedCustomFields = [...customFieldDefinitions.map(def => ({
        id: def.id,
        name: def.name,
        type: def.type,
        value: ''
      }))];
      
      if (opportunity.custom_fields) {
        try {
          const savedFields = JSON.parse(opportunity.custom_fields);
          // Merge saved values with current definitions
          mergedCustomFields = mergedCustomFields.map(field => {
            const savedField = savedFields.find((saved: any) => saved.id === field.id || saved.name === field.name);
            return savedField ? { ...field, value: savedField.value } : field;
          });
        } catch (error) {
          console.error('Error parsing custom fields:', error);
        }
      }
      
      // Populate the form fields with opportunity data
      setSelectedCustomer(customerData || null);
      setOpportunityTitle(opportunity.title);
      setSelectedPipeline(pipeline || null);
      setSelectedStage(stage || null);
      setOpportunityValue(opportunity.value);
      setBusinessName(opportunity.business_name || '');
      setOpportunitySource(opportunity.source || '');
      setCustomFields(mergedCustomFields);
      
      // Open the info modal
      setIsOpportunityInfoModalOpen(true);
    } catch (error) {
      console.error('Error loading opportunity info:', error);
    }
  };

  // Handle closing opportunity info modal
  const handleCloseOpportunityInfo = () => {
    setIsOpportunityInfoModalOpen(false);
    setSelectedOpportunityForInfo(null);
    // Reset all form fields
    setSelectedCustomer(null);
    setOpportunityTitle('');
    setSelectedPipeline(null);
    setSelectedStage(null);
    setOpportunityValue(0);
    setBusinessName('');
    setOpportunitySource('');
    // Reset custom field values but keep the definitions
    initializeCustomFields();
  };

  // Handle updating an existing opportunity
  const handleUpdateOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCustomer || !selectedPipeline || !selectedStage || !selectedOpportunityForInfo || !supabase) {
      console.log('Update validation failed');
      return;
    }

    try {
      console.log('Updating opportunity in database...');
      setIsLoading(true);
      
      const updateData = {
        customer_id: selectedCustomer.id,
        title: opportunityTitle,
        pipeline_id: selectedPipeline.id,
        stage_id: selectedStage.id,
        value: opportunityValue,
        business_name: businessName || null,
        source: opportunitySource || null,
        custom_fields: customFields.length > 0 ? JSON.stringify(customFields) : null,
        updated_at: new Date().toISOString()
      };
      
      console.log('Update data:', updateData);
      
      // Update the opportunity in database
      const { data, error } = await supabase
        .from('pipeline_opportunities')
        .update(updateData)
        .eq('id', selectedOpportunityForInfo.id)
        .select()
        .single();

      console.log('Update response:', { data, error });

      if (error) throw error;

      // Update the opportunity in local state
      setOpportunities(prev => 
        prev.map(opp => opp.id === selectedOpportunityForInfo.id ? data : opp)
      );
      
      console.log('Opportunity updated successfully:', data);
      
      // Close the modal
      handleCloseOpportunityInfo();
      
    } catch (err) {
      console.error('Error updating opportunity:', err);
      setError('Failed to update opportunity. Please try again.');
      alert('Error updating opportunity: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="-mt-4 -ml-4 -mr-4 sm:-mx-6 md:-mx-8 min-h-screen flex flex-col">
      {/* CRM Navigation Header */}
      <div className="bg-white w-full shadow-sm border-b border-gray-200 px-6 py-5 z-10">
        <div className="w-full flex items-center h-12">
          <h2 className="text-xl font-semibold text-gray-800 mr-8">CRM</h2>
          <div className="flex space-x-6">
            <button 
              onClick={() => setActiveTab('opportunities')}
              className={`text-base font-medium py-1 border-b-2 transition-colors ${
                activeTab === 'opportunities' 
                  ? 'text-blue-600 border-blue-600' 
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              Opportunities
            </button>
            <button
              onClick={() => setActiveTab('pipelines')}
              className={`text-base font-medium py-1 border-b-2 transition-colors ${
                activeTab === 'pipelines' 
                  ? 'text-blue-600 border-blue-600' 
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              Pipelines
            </button>
          </div>
        </div>
      </div>
      
      {/* Page content */}
      <div className="bg-gray-50 p-4 sm:p-6 flex-1">
        {activeTab === 'opportunities' && (
          <div className="bg-gray-50">
            {/* Top bar with pipeline selector and actions */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <select 
                    className="appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-gray-700 cursor-pointer focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={opportunitiesViewPipeline?.id || ""}
                    onChange={(e) => {
                      const pipeline = pipelines.find(p => p.id.toString() === e.target.value);
                      setOpportunitiesViewPipeline(pipeline || null);
                    }}
                  >
                    {pipelines.map(pipeline => (
                      <option key={pipeline.id} value={pipeline.id}>
                        {pipeline.pipeline_name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                    </svg>
                  </div>
                </div>
                <span className="text-blue-500 text-sm">0 opportunities</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex border border-gray-300 rounded-md overflow-hidden">
                  <button 
                    className={`px-2 py-1 ${viewMode === 'all' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700'}`}
                    onClick={() => setViewMode('all')}
                  >
                    <Grid size={18} />
                  </button>
                  <button 
                    className={`px-2 py-1 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700'}`}
                    onClick={() => setViewMode('list')}
                  >
                    <List size={18} />
                  </button>
                </div>
                
                <button className="flex items-center space-x-2 border border-gray-300 bg-white text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-50">
                  <Download size={16} />
                  <span>Import</span>
                </button>
                
                <button 
                  onClick={() => handleOpenOpportunityModal(opportunitiesViewPipeline || undefined)}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700"
                >
                  <Plus size={16} />
                  <span>Add opportunity</span>
                </button>
              </div>
            </div>
            
            {/* Filters & Search row */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-2">
                <button className="flex items-center space-x-2 bg-white border border-gray-300 px-3 py-1.5 rounded-md text-gray-700 hover:bg-gray-50">
                  <ListFilter size={16} />
                  <span>Advanced Filters</span>
                </button>
                
                <button className="flex items-center space-x-2 bg-white border border-gray-300 px-3 py-1.5 rounded-md text-gray-700 hover:bg-gray-50">
                  <ArrowDownUp size={16} />
                  <span>Sort (1)</span>
                </button>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search Opportunities"
                    className="pl-9 pr-4 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-60"
                  />
                  <div className="absolute left-0 inset-y-0 flex items-center pl-3 pointer-events-none">
                    <Search size={16} className="text-gray-400" />
                  </div>
                </div>
                
                <button className="flex items-center space-x-2 bg-white border border-gray-300 px-3 py-1.5 rounded-md text-gray-700 hover:bg-gray-50">
                  <Settings size={16} />
                  <span>Manage Fields</span>
                </button>
              </div>
            </div>
            
            {/* Kanban board / Pipeline view */}
            {viewMode === 'all' && (
              <div className="mt-4 overflow-hidden">
                <div className="overflow-x-auto -mx-4 px-4">
                  {renderStageColumns()}
                </div>
                {opportunities.length === 0 && (
                  <div className="mt-8">
                    {renderNoOpportunitiesState()}
                  </div>
                )}
              </div>
            )}
            
            {/* List view (empty for now) */}
            {viewMode === 'list' && (
              <div className="mt-4">
                {opportunities.length === 0 && (
                  <div>
                    {renderNoOpportunitiesState()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'pipelines' && (
          <div className="bg-gray-50">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-medium text-gray-700">Pipelines</h2>
              <button 
                onClick={() => handleOpenModal()}
                className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                disabled={isLoading}
              >
                <Plus size={18} />
                Create new pipeline
              </button>
            </div>
            
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
                {error}
              </div>
            )}
            
            {isLoading && !isModalOpen ? (
              <div className="flex justify-center items-center h-40">
                <Loader className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : pipelines.length === 0 ? (
              <div className="bg-white rounded-md shadow-sm p-8 text-center text-gray-500">
                No pipelines found. Create a new pipeline to get started.
              </div>
            ) : (
              <div className="bg-white rounded-md shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pipelines.map((pipeline) => (
                      <tr key={pipeline.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {pipeline.pipeline_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-3">
                            <button 
                              className="text-gray-400 hover:text-gray-500"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenModal(true, pipeline);
                              }}
                            >
                              <Edit size={18} />
                            </button>
                            <button 
                              className="text-gray-400 hover:text-gray-500"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePipeline(pipeline.id);
                              }}
                            >
                              <Trash size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Pipeline Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-auto">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                {isEditMode ? (
                  <>
                    <Edit size={18} className="mr-2" /> Edit pipeline
                  </>
                ) : (
                  <>
                    <Plus size={18} className="mr-2" /> Add pipeline
                  </>
                )}
              </h3>
              <button 
                onClick={handleCloseModal} 
                className="text-gray-400 hover:text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-6">
                <label htmlFor="pipeline-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Pipeline Name
                </label>
                <input
                  type="text"
                  id="pipeline-name"
                  placeholder="Name"
                  value={pipelineName}
                  onChange={(e) => setPipelineName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Stage Name
                  </label>
                  <div className="text-sm font-medium text-gray-700">
                    Actions
                  </div>
                </div>

                {stages
                  .sort((a, b) => (a.position || 0) - (b.position || 0))
                  .map((stage, index) => (
                  <div 
                    key={stage.id} 
                    className="flex items-center mb-2"
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, stage.id)}
                    onDragOver={(e) => handleDragOver(e, stage.id)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="mr-2 cursor-grab text-gray-400 hover:text-gray-600" title="Drag to reorder">
                      <GripVertical size={16} />
                    </div>
                    <input
                      type="text"
                      placeholder="Stage Name"
                      value={stage.name}
                      onChange={(e) => updateStageName(stage.id, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="flex ml-4 space-x-2">
                      <button type="button" className="text-green-500 hover:text-green-600">
                        <PieChart size={18} />
                      </button>
                      <button type="button" className="text-green-500 hover:text-green-600">
                        <Filter size={18} />
                      </button>
                      <button 
                        type="button" 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeStage(stage.id);
                        }}
                        className="text-red-500 hover:text-red-600"
                        disabled={stages.length <= 1}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    addStage();
                  }}
                  className="flex items-center text-blue-500 hover:text-blue-600 mt-2"
                >
                  <Plus size={18} className="mr-1" /> Add stage
                </button>
              </div>

              <div className="border-t border-gray-200 pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-500 hover:bg-green-600 focus:outline-none flex items-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader size={16} className="animate-spin mr-2" />
                      {isEditMode ? 'Updating...' : 'Saving...'}
                    </>
                  ) : (
                    isEditMode ? 'Update' : 'Save'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Opportunity Modal */}
      {isOpportunityModalOpen && (
        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-300 ${
            isOpportunityModalOpen ? 'bg-opacity-50' : 'bg-opacity-0'
          } z-[10000]`}
          onClick={handleModalClose}
        >
          <div 
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
              isOpportunityModalOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="h-full flex flex-col">
              <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h2 className="text-lg font-semibold text-white">Create Opportunity</h2>
                </div>
                <button
                  onClick={handleModalClose}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form 
                className="flex-1 overflow-y-auto p-6"
                onSubmit={handleCreateOpportunity}
              >
                <div className="space-y-8">
                  {/* Customer Selection Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Customer
                    </label>
                    {selectedCustomer ? (
                      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-sm font-medium text-white">
                              {selectedCustomer.first_name[0]}{selectedCustomer.last_name[0]}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {selectedCustomer.first_name} {selectedCustomer.last_name}
                              </div>
                              {selectedCustomer.email && (
                                <div className="text-sm text-gray-500">
                                  {selectedCustomer.email}
                                </div>
                              )}
                              {selectedCustomer.phone && (
                                <div className="text-sm text-gray-500">
                                  {selectedCustomer.phone}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsCustomerModalOpen(true);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Change
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsCustomerModalOpen(true);
                        }}
                        className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Select Customer
                      </button>
                    )}
                  </div>

                  {/* Opportunity Title Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Opportunity Title
                    </label>
                    <input
                      type="text"
                      placeholder="Enter opportunity title"
                      value={opportunityTitle}
                      onChange={(e) => setOpportunityTitle(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Pipeline Selection Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Pipeline
                    </label>
                    <div className="relative">
                      <select
                        value={selectedPipeline?.id || ''}
                        onChange={(e) => {
                          const pipeline = pipelines.find(p => p.id.toString() === e.target.value);
                          handlePipelineSelect(pipeline || null);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                      >
                        <option value="">Select a pipeline</option>
                        {pipelines.map(pipeline => (
                          <option key={pipeline.id} value={pipeline.id}>
                            {pipeline.pipeline_name}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Stage Selection Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Stage *
                    </label>
                    <div className="relative">
                      <select
                        value={selectedStage?.id || ''}
                        onChange={(e) => {
                          const stage = selectedPipeline?.stages?.find(s => s.id.toString() === e.target.value);
                          setSelectedStage(stage || null);
                        }}
                        disabled={!selectedPipeline || !selectedPipeline.stages || selectedPipeline.stages.length === 0}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">Select a stage</option>
                        {selectedPipeline?.stages?.map(stage => (
                          <option key={stage.id} value={stage.id}>
                            {stage.name}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                        </svg>
                      </div>
                    </div>
                    {!selectedPipeline && (
                      <p className="mt-1 text-sm text-gray-500">Please select a pipeline first</p>
                    )}
                  </div>

                  {/* Opportunity Value Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Opportunity Value
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={opportunityValue}
                        onChange={(e) => setOpportunityValue(Number(e.target.value) || 0)}
                        className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Business Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Business Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter business name"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Opportunity Source Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Opportunity Source
                    </label>
                    <input
                      type="text"
                      placeholder="Enter opportunity source"
                      value={opportunitySource}
                      onChange={(e) => setOpportunitySource(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Custom Fields Display */}
                  {customFieldDefinitions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Custom Fields
                      </label>
                      <div className="space-y-3">
                        {customFields.map(field => (
                          <div key={field.id}>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-xs font-medium text-gray-600">
                                {field.name}
                              </label>
                              <button
                                type="button"
                                onClick={() => handleRemoveCustomField(field.id)}
                                className="text-red-500 hover:text-red-700 text-xs"
                                title="Remove this custom field"
                              >
                                Remove
                              </button>
                            </div>
                            {field.type === 'text' && (
                              <input
                                type="text"
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                              />
                            )}
                            {field.type === 'number' && (
                              <input
                                type="number"
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                              />
                            )}
                            {field.type === 'textarea' && (
                              <textarea
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                                rows={2}
                              />
                            )}
                            {field.type === 'date' && (
                              <input
                                type="date"
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional opportunity fields will be added here */}
                  <div className="text-center py-8 text-gray-500">
                    <p>Additional opportunity fields will be added here</p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => setIsCustomFieldsModalOpen(true)}
                        className="flex items-center text-gray-500 hover:text-gray-700 text-sm"
                        title="Add Custom fields"
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        <span>Add Custom fields</span>
                      </button>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handleModalClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!selectedCustomer || !selectedPipeline || !selectedStage}
                        className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Create Opportunity
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Select Customer Modal */}
      {isCustomerModalOpen && (
        <SelectCustomerModal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSelect={handleCustomerSelect}
        />
      )}

      {/* Opportunity Info Modal */}
      {isOpportunityInfoModalOpen && (
        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-300 ${
            isOpportunityInfoModalOpen ? 'bg-opacity-50' : 'bg-opacity-0'
          } z-[10000]`}
          onClick={handleCloseOpportunityInfo}
        >
          <div 
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
              isOpportunityInfoModalOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="h-full flex flex-col">
              <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h2 className="text-lg font-semibold text-white">Opportunity Information</h2>
                </div>
                <button
                  onClick={handleCloseOpportunityInfo}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form 
                className="flex-1 overflow-y-auto p-6"
                onSubmit={handleUpdateOpportunity}
              >
                <div className="space-y-8">
                  {/* Customer Selection Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Customer
                    </label>
                    {selectedCustomer ? (
                      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-sm font-medium text-white">
                              {selectedCustomer.first_name[0]}{selectedCustomer.last_name[0]}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {selectedCustomer.first_name} {selectedCustomer.last_name}
                              </div>
                              {selectedCustomer.email && (
                                <div className="text-sm text-gray-500">
                                  {selectedCustomer.email}
                                </div>
                              )}
                              {selectedCustomer.phone && (
                                <div className="text-sm text-gray-500">
                                  {selectedCustomer.phone}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsCustomerModalOpen(true);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Change
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsCustomerModalOpen(true);
                        }}
                        className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Select Customer
                      </button>
                    )}
                  </div>

                  {/* Opportunity Title Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Opportunity Title
                    </label>
                    <input
                      type="text"
                      placeholder="Enter opportunity title"
                      value={opportunityTitle}
                      onChange={(e) => setOpportunityTitle(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Pipeline Selection Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Pipeline
                    </label>
                    <div className="relative">
                      <select
                        value={selectedPipeline?.id || ''}
                        onChange={(e) => {
                          const pipeline = pipelines.find(p => p.id.toString() === e.target.value);
                          handlePipelineSelect(pipeline || null);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                      >
                        <option value="">Select a pipeline</option>
                        {pipelines.map(pipeline => (
                          <option key={pipeline.id} value={pipeline.id}>
                            {pipeline.pipeline_name}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Stage Selection Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Stage *
                    </label>
                    <div className="relative">
                      <select
                        value={selectedStage?.id || ''}
                        onChange={(e) => {
                          const stage = selectedPipeline?.stages?.find(s => s.id.toString() === e.target.value);
                          setSelectedStage(stage || null);
                        }}
                        disabled={!selectedPipeline || !selectedPipeline.stages || selectedPipeline.stages.length === 0}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">Select a stage</option>
                        {selectedPipeline?.stages?.map(stage => (
                          <option key={stage.id} value={stage.id}>
                            {stage.name}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                        </svg>
                      </div>
                    </div>
                    {!selectedPipeline && (
                      <p className="mt-1 text-sm text-gray-500">Please select a pipeline first</p>
                    )}
                  </div>

                  {/* Opportunity Value Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Opportunity Value
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={opportunityValue}
                        onChange={(e) => setOpportunityValue(Number(e.target.value) || 0)}
                        className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Business Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Business Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter business name"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Opportunity Source Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Opportunity Source
                    </label>
                    <input
                      type="text"
                      placeholder="Enter opportunity source"
                      value={opportunitySource}
                      onChange={(e) => setOpportunitySource(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Custom Fields Display */}
                  {customFieldDefinitions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Custom Fields
                      </label>
                      <div className="space-y-3">
                        {customFields.map(field => (
                          <div key={field.id}>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-xs font-medium text-gray-600">
                                {field.name}
                              </label>
                              <button
                                type="button"
                                onClick={() => handleRemoveCustomField(field.id)}
                                className="text-red-500 hover:text-red-700 text-xs"
                                title="Remove this custom field"
                              >
                                Remove
                              </button>
                            </div>
                            {field.type === 'text' && (
                              <input
                                type="text"
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                              />
                            )}
                            {field.type === 'number' && (
                              <input
                                type="number"
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                              />
                            )}
                            {field.type === 'textarea' && (
                              <textarea
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                                rows={2}
                              />
                            )}
                            {field.type === 'date' && (
                              <input
                                type="date"
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional opportunity fields will be added here */}
                  <div className="text-center py-8 text-gray-500">
                    <p>Additional opportunity fields will be added here</p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => setIsCustomFieldsModalOpen(true)}
                        className="flex items-center text-gray-500 hover:text-gray-700 text-sm"
                        title="Add Custom fields"
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        <span>Add Custom fields</span>
                      </button>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handleCloseOpportunityInfo}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!selectedCustomer || !selectedPipeline || !selectedStage}
                        className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? 'Updating...' : 'Update Opportunity'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Custom Fields Modal */}
      {isCustomFieldsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-lg w-full max-w-md mx-auto">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Settings size={18} className="mr-2" /> Add Custom Field
              </h3>
              <button 
                onClick={() => setIsCustomFieldsModalOpen(false)} 
                className="text-gray-400 hover:text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const name = formData.get('fieldName') as string;
                const type = formData.get('fieldType') as string;
                
                if (name.trim()) {
                  handleAddCustomField(name.trim(), type);
                  setIsCustomFieldsModalOpen(false);
                  (e.target as HTMLFormElement).reset();
                }
              }}
              className="p-6"
            >
              <div className="space-y-4">
                <div>
                  <label htmlFor="fieldName" className="block text-sm font-medium text-gray-700 mb-2">
                    Field Name
                  </label>
                  <input
                    type="text"
                    id="fieldName"
                    name="fieldName"
                    placeholder="Enter field name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="fieldType" className="block text-sm font-medium text-gray-700 mb-2">
                    Field Type
                  </label>
                  <select
                    id="fieldType"
                    name="fieldType"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="textarea">Text Area</option>
                    <option value="date">Date</option>
                  </select>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsCustomFieldsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                >
                  Add Field
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 